import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerLocale } from "@/lib/translator";
import useReturnRequestsTimeChart from "@babackapp/private-api-client/client/use-return-requests-time-chart";
import { ReturnRequestsTimeChartItem } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import { Bar, BarChart, CartesianGrid, Cell, XAxis } from "recharts";
import { useDashboardFilters } from "../lib/dashboard-filters";
import useFormattedTimeRange from "../lib/use-formatted-time-range";
import {
  discountColor,
  exchangeColor,
  exchangeCreditColor,
  refundColor,
  unknownColor,
} from "./colors";
import { startOfWeek, addWeeks, getWeek } from "date-fns";

function getDateFromYearAndWeek(year: number, week: number): Date {
  const firstDayOfYear = new Date(year, 0, 1);
  const firstMonday = startOfWeek(firstDayOfYear, { weekStartsOn: 1 });
  return addWeeks(firstMonday, week - 1);
}

export function ReturnRequestEvolutionChart() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const {
    toTimestamp,
    fromTimestamp,
    productId,
    variantId,
    resolution,
    reason,
  } = useDashboardFilters();

  const timeChartConfig = {
    discount: {
      label: t("analyticsDashboard.resolutionTypes.discount"),
      color: discountColor,
    },
    exchange: {
      label: t("analyticsDashboard.resolutionTypes.exchange"),
      color: exchangeColor,
    },
    exchangeCredit: {
      label: t("analyticsDashboard.resolutionTypes.exchangeCredit"),
      color: exchangeCreditColor,
    },
    refund: {
      label: t("analyticsDashboard.resolutionTypes.refund"),
      color: refundColor,
    },
    unknown: {
      label: t("analyticsDashboard.resolutionTypes.other"),
      color: unknownColor,
    },
  } satisfies ChartConfig;

  const getTopNonZeroBar = (data: ReturnRequestsTimeChartItem) => {
    let topBarKey = null;
    Object.keys(data).forEach((key) => {
      if (
        key !== "date" &&
        +data[key as keyof typeof data]?.count > 0 // Handle count values
      ) {
        topBarKey = key;
      }
    });
    return topBarKey;
  };

  let { data: timeChartData } = useReturnRequestsTimeChart(shop.id, {
    fromTimestamp: fromTimestamp ?? 0,
    toTimestamp: toTimestamp ?? 0,
    productId,
    variantId,
    resolution,
    reason,
  });

  timeChartData ??= [];

  const formattedTimeRange = useFormattedTimeRange();
  const locale = useViewerLocale();

  return (
    <Card className="col-span-3 flex-1">
      <CardHeader>
        <CardTitle className="text-md">
          {t("analyticsDashboard.evolution.title")}
        </CardTitle>
        <CardDescription>{formattedTimeRange}</CardDescription>
      </CardHeader>
      <CardContent className="h-80 w-full">
        <ChartContainer config={timeChartConfig} className="h-full w-full">
          <BarChart
            className="w-full"
            accessibilityLayer
            data={timeChartData}
            width={900}
            height={300}
            style={{ width: "100%" }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  hideLabel={false}
                  formatter={(value, name, item) => {
                    if (typeof name !== "string" || !value) return null;

                    const [key] = name.split(".");
                    const resolutionData = item.payload[key];

                    return (
                      <>
                        <div className="flex gap-2 items-center">
                          <div
                            className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-(--color-bg)"
                            style={
                              {
                                "--color-bg":
                                  timeChartConfig[
                                    key as keyof typeof timeChartConfig
                                  ]?.color,
                              } as React.CSSProperties
                            }
                          />
                          <span>
                            {timeChartConfig[
                              key as keyof typeof timeChartConfig
                            ]?.label || key}
                            {": "}
                            <span className="font-semibold">
                              {value}{" "}
                              {resolutionData && (
                                <span className="text-green-500 text-xs">
                                  {Intl.NumberFormat(locale, {
                                    style: "currency",
                                    currency: shop.preferences.currency,
                                  }).format(resolutionData.value)}
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      </>
                    );
                  }}
                />
              }
            />
            <XAxis
              dataKey="date"
              tickLine={true}
              tickMargin={10}
              interval={0}
              tickFormatter={(value, index) => {
                const [_, week, day] = value.split("-");
                if (day) return parseInt(day) % 7 === 1 ? day : "";
                return week % 3 === 1 ? week : "";
              }}
            />
            {Object.keys(timeChartConfig).map((key) => (
              <Bar
                key={key}
                dataKey={`${key}.count`}
                stackId="a"
                fill={
                  timeChartConfig[key as keyof typeof timeChartConfig].color
                }
              >
                {timeChartData.map((entry, index) => {
                  const topBarKey = getTopNonZeroBar(entry);
                  const isTopBar = topBarKey === key;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        timeChartConfig[key as keyof typeof timeChartConfig]
                          .color
                      }
                      // @ts-expect-error not typed
                      radius={isTopBar ? [8, 8, 0, 0] : [0, 0, 0, 0]}
                    />
                  );
                })}
              </Bar>
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
